import React, { useRef } from 'react'
import { bool, string, object } from 'prop-types'
import useMedia from 'use-media'
import { css } from '@emotion/react'
import { canUseWindow } from 'gatsby-theme-marketplace-base/src/utils/browser/window'
import { KEY_CODES } from 'gatsby-theme-marketplace-base/src/constants'
import BoxesIcon from 'gatsby-theme-marketplace-base/src/assets/icons/boxes.inline.svg'
import useOutsideClick from 'gatsby-theme-marketplace-base/src/hooks/useOutsideClick'
import useInViewport from 'gatsby-theme-marketplace-base/src/hooks/useInViewport'
import useHydrationContext from 'gatsby-theme-marketplace-base/src/hooks/useHydrationContext'
import CartActionButton from 'gatsby-theme-marketplace-base/src/components/Cart/Summary/Action'
import AppActionButton from 'gatsby-theme-marketplace-base/src/components/Product/Action'
import AppBreadcrumbs from 'gatsby-theme-marketplace-base/src/components/Product/Breadcrumbs'
import ExpertBreadcrumbs from 'gatsby-theme-marketplace-base/src/components/Expert/Breadcrumbs'
import DeveloperBreadcrumbs from 'gatsby-theme-marketplace-base/src/components/Developer/Breadcrumbs'
import Cart from 'gatsby-theme-marketplace-base/src/components/Navigation/Cart'

import mediaQueries from '../layout/media-queries'
import MainSidebar from './MainSidebar'

const iconCss = (theme) => css`
  margin-right: 5px;
  fill: ${theme.colors.gray900};
`

const inputIdName = 'sidenav-toggle'

const browseBtnCss = css`
  min-width: 90px;

  & > div {
    cursor: pointer;
    display: flex;
    align-items: center;
  }

  &:focus > div {
    outline: -webkit-focus-ring-color auto 5px;
  }

  &:focus,
  & > div:focus {
    outline: none;
  }

  #${inputIdName}:checked + & + .sidebar_wrapper {
    transform: translateX(0);
  }

  ${mediaQueries.smallScreen(-280)} {
    display: none;
  }
`

const sidebarWrapperCss = (theme) => css`
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  max-width: ${theme.sidebar.maxWidth};
  transform: translateX(calc(-${theme.sidebar.maxWidth} - 10px));
  transition: transform 0.25s cubic-bezier(0.17, 0.67, 0.88, 0.72);
  box-shadow: ${theme.boxShadows.sidebar};
  z-index: 1000;

  ${mediaQueries.smallScreen(-280)} {
    transform: translateX(0);
    box-shadow: inset ${theme.boxShadows.sidebar};
  }
`

const navCss = (showSidebarMenu) => (theme) =>
  css`
    position: relative;
    height: 52px;
    padding: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: ${theme.colors.white};
    border-bottom: 1px solid ${theme.colors.gray100};

    ${!showSidebarMenu && `justify-content: center;`}

    ${mediaQueries.smallScreen(-280)} {
      ${showSidebarMenu && `margin-left: ${theme.sidebar.maxWidth};`}
      justify-content: flex-end;
    }
  `

const inputCss = css`
  display: none;
`

const actionsCss = () => css`
  position: absolute;
  right: 16px;
  display: flex;
  justify-content: flex-end;
  text-align: right;
  gap: 16px;

  .minimalistic-loader {
    min-width: 100px;
  }
`

const wrapperCss = css`
  position: sticky;
  display: flex;
  flex-direction: column;
  z-index: 1000;
  top: 0;
  left: 0;
  width: 100%;
`

const headerLabelCss = (showSidebarMenu) => css`
  ${showSidebarMenu && `display: none;`}

  ${mediaQueries.smallScreen(-280)} {
    display: block;
    flex-grow: 1;
    text-align: center;
  }
`

const headerLabelLeftAlignCss = css`
  ${mediaQueries.smallScreen(-280)} {
    text-align: left;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    padding-left: 12px;
    margin-right: 160px;
  }
`

const breadcrumbsWrapperCss = css`
  display: block;

  ${mediaQueries.smallScreen(-280)} {
    display: none;
  }
`

const SidebarNav = ({
  app,
  expert,
  category,
  service,
  developer,
  cart,
  headerLabel,
  showCart,
  showSidebarMenu,
}) => {
  const hydrated = useHydrationContext()

  const inputRef = useRef(null)
  const wrapperRef = useRef(null)

  let selector

  if (app || service) {
    selector = '#action-btn-app'
  } else if (cart) {
    selector = '#action-btn-cart'
  }

  const isSideBarVisible = useMedia(
    {
      minWidth: mediaQueries.BASE_SMALL_SIZE_NO_SIDEBAR,
    },
    canUseWindow && window.innerWidth > 768 ? true : false
  )

  const offSetTop = () => (isSideBarVisible ? -60 : -105)

  const isActionButtonVisible = useInViewport(selector, {
    offsetTop: offSetTop(),
    disable: !(app || service) && !cart,
  })

  useOutsideClick(() => {
    toggleOpen()
  }, wrapperRef)

  const toggleOpen = () => {
    if (inputRef.current && inputRef.current.checked) {
      inputRef.current.click()
    } else {
      inputRef.current.focus()
    }
  }

  const handleKeyPress = (e) => {
    switch (e.charCode) {
      case KEY_CODES.SPACEBAR:
      case KEY_CODES.ENTER:
        inputRef.current.click()
        break

      default:
    }
  }

  const isExpertsView =
    hydrated &&
    canUseWindow &&
    String(window.location.pathname).includes('/partners')

  const isDeveloperView =
    hydrated &&
    canUseWindow &&
    String(window.location.pathname).includes('/developers')

  const baseHeaderCss = headerLabelCss(showSidebarMenu)
  const headerCss = isActionButtonVisible
    ? baseHeaderCss
    : app || service
    ? [baseHeaderCss, headerLabelLeftAlignCss]
    : baseHeaderCss

  // TODO: refactor this logic, and move it out of this component to app/service/cart etc
  const getActionBtn = () => {
    if (
      typeof isActionButtonVisible !== 'undefined' &&
      !isActionButtonVisible
    ) {
      if (app) {
        return (
          <AppActionButton
            useActionQuery={false}
            id={app.id}
            size="compact"
            fullWidth={false}
            type="link"
            className="header-action"
            minimalisticLoader
          />
        )
      } else if (service) {
        return (
          <AppActionButton
            useActionQuery={false}
            id={service.id}
            size="compact"
            fullWidth={false}
            type="link"
            className="header-action"
            minimalisticLoader
          />
        )
      } else if (cart) {
        const emptyCart = !cart.products || !cart.products.length
        return (
          <CartActionButton
            cartId={cart.id}
            size="compact"
            disabled={emptyCart}
          />
        )
      } else {
        return null
      }
    }
  }

  return (
    <div css={wrapperCss}>
      <div css={navCss(showSidebarMenu)} ref={wrapperRef}>
        <input css={inputCss} id={inputIdName} type="checkbox" ref={inputRef} />

        {showSidebarMenu && (
          <>
            <label
              css={browseBtnCss}
              onKeyPress={handleKeyPress}
              htmlFor={inputIdName}
              tabIndex="0"
              onClick={toggleOpen}
            >
              <div tabIndex="-1">
                <BoxesIcon css={iconCss} />
                <span>Browse</span>
              </div>
            </label>

            <div className="sidebar_wrapper" css={sidebarWrapperCss}>
              <MainSidebar onToggleSidebar={toggleOpen} title="Marketplace" />
            </div>
          </>
        )}

        <div css={headerCss}>{headerLabel}</div>

        <div css={actionsCss}>
          {getActionBtn()}

          {showCart && <Cart showLabel={!app && !service} />}
        </div>
      </div>

      {(app || category || service) && (
        <div css={breadcrumbsWrapperCss}>
          <AppBreadcrumbs
            category={category}
            {...app}
            {...service}
            isServiceView={!!service}
          />
        </div>
      )}

      {isExpertsView && (
        <div css={breadcrumbsWrapperCss}>
          <ExpertBreadcrumbs expert={expert} />
        </div>
      )}

      {isDeveloperView && (
        <div css={breadcrumbsWrapperCss}>
          <DeveloperBreadcrumbs developer={developer} />
        </div>
      )}
    </div>
  )
}

SidebarNav.propTypes = {
  app: object,
  expert: object,
  service: object,
  category: object,
  developer: object,
  cart: object,
  headerLabel: string,
  showCart: bool,
  showSidebarMenu: bool,
}

export default SidebarNav
